import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Button,
  Box,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Link,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useApiGet, useApiSend } from '../../utils/httpClientTs';
import {
  getExecutiveOfficeGroups,
  createExecutiveOfficeGroup,
  EOGroupSlot,
  EOGroup,
  EOGroupMember,
} from '../../endpoints/executiveOffice';
import { getGroupRolesByType } from '../../endpoints/groups';
import {
  addLocalChapterCommitteeMember,
  updateLocalChapterCommitteeMember,
  deleteLocalChapterCommitteeMember,
} from '../../endpoints/chaptersTs';
import {
  LocalChapterCommitteeMemberPost,
  LocalChapterCommitteeMemberPut,
} from '../../types/committee';
import UserSearchModal from '../../Components/UserSearchModal';
import dayjs from 'dayjs';

const ExecutiveOffice: React.FC = () => {
  const [slots, setSlots] = useState<EOGroupSlot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [initCreating, setInitCreating] = useState<string | null>(null);
  const [addGroupModal, setAddGroupModal] = useState<{
    groupTypeId: string;
    typeName: string;
  } | null>(null);
  const [newGroupName, setNewGroupName] = useState('');
  const [addModal, setAddModal] = useState<{
    groupId: string;
    groupTypeId: string;
  } | null>(null);
  const [editModal, setEditModal] = useState<{
    groupId: string;
    member: EOGroupMember;
  } | null>(null);
  const [selectedUser, setSelectedUser] = useState<{
    id: string;
    first_name?: string;
    last_name?: string;
  } | null>(null);
  const [userSearchOpen, setUserSearchOpen] = useState(false);
  const [positionId, setPositionId] = useState('');
  const [startDate, setStartDate] = useState(dayjs().format('YYYY-MM-DD'));
  const [endDate, setEndDate] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const {
    data: slotsData,
    isLoading,
    refetch,
  } = useApiGet<EOGroupSlot[]>(
    ['executive-office-groups'],
    getExecutiveOfficeGroups,
    { enabled: true, retry: 1 }
  );

  React.useEffect(() => {
    if (slotsData) setSlots(slotsData);
  }, [slotsData]);

  const { mutate: createGroupMutate } = useApiSend(
    (payload: { group_type_id: string; name?: string }) =>
      createExecutiveOfficeGroup(payload),
    () => {
      setInitCreating(null);
      setAddGroupModal(null);
      setNewGroupName('');
      setIsSaving(false);
      setError(null);
      refetch();
    },
    (err: Error) => {
      setError(err.message || 'Failed to create group');
      setIsSaving(false);
      setInitCreating(null);
    }
  );

  const { mutate: addMemberMutate } = useApiSend(
    (payload: { groupId: string; data: LocalChapterCommitteeMemberPost }) =>
      addLocalChapterCommitteeMember(payload.groupId, payload.data),
    () => {
      setAddModal(null);
      setSelectedUser(null);
      setPositionId('');
      setStartDate(dayjs().format('YYYY-MM-DD'));
      setEndDate('');
      setIsSaving(false);
      setError(null);
      refetch();
    },
    (err: Error) => {
      setError(err.message || 'Failed to add member');
      setIsSaving(false);
    }
  );

  const { mutate: updateMemberMutate } = useApiSend(
    (payload: {
      groupId: string;
      memberId: string;
      data: LocalChapterCommitteeMemberPut;
    }) =>
      updateLocalChapterCommitteeMember(
        payload.groupId,
        payload.memberId,
        payload.data
      ),
    () => {
      setEditModal(null);
      setIsSaving(false);
      setError(null);
      refetch();
    },
    (err: Error) => {
      setError(err.message || 'Failed to update member');
      setIsSaving(false);
    }
  );

  const { mutate: deleteMemberMutate } = useApiSend(
    (payload: { groupId: string; memberId: string }) =>
      deleteLocalChapterCommitteeMember(payload.groupId, payload.memberId),
    () => {
      setIsSaving(false);
      setError(null);
      refetch();
    },
    (err: Error) => {
      setError(err.message || 'Failed to remove member');
      setIsSaving(false);
    }
  );

  const handleOpenAddGroup = (groupTypeId: string, typeName: string) => {
    setError(null);
    setAddGroupModal({ groupTypeId, typeName });
    setNewGroupName('');
  };

  const handleAddGroup = () => {
    if (!addGroupModal) return;
    setError(null);
    setInitCreating(addGroupModal.groupTypeId);
    setIsSaving(true);
    createGroupMutate({
      group_type_id: addGroupModal.groupTypeId,
      name: newGroupName.trim() || undefined,
    });
  };

  const handleOpenAddMember = (groupId: string, groupTypeId: string) => {
    setAddModal({ groupId, groupTypeId });
    setSelectedUser(null);
    setPositionId('');
    setStartDate(dayjs().format('YYYY-MM-DD'));
    setEndDate('');
  };

  const handleAddMember = () => {
    if (!addModal || !selectedUser || !positionId || !startDate) {
      setError('Please select a user, role, and start date.');
      return;
    }
    setError(null);
    setIsSaving(true);
    addMemberMutate({
      groupId: addModal.groupId,
      data: {
        user_id: selectedUser.id,
        position_id: positionId,
        start_date: startDate,
        end_date: endDate || '',
      },
    });
  };

  const handleOpenEditMember = (groupId: string, member: EOGroupMember) => {
    setEditModal({ groupId, member });
    setEditStartDate(member.start_date);
    setEditEndDate(member.end_date || '');
  };

  const handleUpdateMember = () => {
    if (!editModal) return;
    setIsSaving(true);
    setError(null);
    updateMemberMutate({
      groupId: editModal.groupId,
      memberId: editModal.member.id,
      data: {
        start_date: editStartDate,
        end_date: editEndDate || '',
      },
    });
  };

  const handleDeleteMember = (groupId: string, member: EOGroupMember) => {
    if (!window.confirm(`Remove ${member.user_name} from this group?`)) return;
    setIsSaving(true);
    setError(null);
    deleteMemberMutate({ groupId, memberId: member.id });
  };

  if (isLoading) {
    return (
      <Grid container padding={2} justifyContent="center">
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Grid container paddingLeft={2} paddingRight={2}>
      <Grid item xs={12} padding={2}>
        <Typography variant="h4" color="primary">
          Executive Office
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Manage EO groups and members. Only EO Admins can make changes.
        </Typography>
      </Grid>
      {error && (
        <Grid item xs={12} sx={{ mb: 2 }}>
          <Alert severity="error" onClose={() => setError(null)}>
            {error}
          </Alert>
        </Grid>
      )}
      {slots.map((slot) => (
        <Grid item xs={12} md={6} key={slot.group_type_id} sx={{ p: 1 }}>
          <Card sx={{ p: 2, boxShadow: 3, borderRadius: 2 }}>
            <CardHeader
              title={slot.name}
              action={
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddIcon />}
                  disabled={isSaving && initCreating === slot.group_type_id}
                  onClick={() =>
                    handleOpenAddGroup(slot.group_type_id, slot.name)
                  }
                >
                  {isSaving && initCreating === slot.group_type_id
                    ? 'Creating…'
                    : 'Add group'}
                </Button>
              }
            />
            <CardContent>
              {!slot.groups || slot.groups.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No groups yet. Add a group to get started.
                </Typography>
              ) : (
                <Grid container spacing={2}>
                  {slot.groups.map((group) => (
                    <Grid item xs={12} key={group.id}>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1.5,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>
                          {group.name}
                        </Typography>
                        <EOGroupSection
                          group={group}
                          groupTypeId={slot.group_type_id}
                          onOpenAddMember={handleOpenAddMember}
                          onOpenEditMember={handleOpenEditMember}
                          onDeleteMember={handleDeleteMember}
                          isSaving={isSaving}
                        />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}

      {/* Add group modal */}
      <Dialog
        open={!!addGroupModal}
        onClose={() => !isSaving && setAddGroupModal(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add group</DialogTitle>
        <DialogContent>
          {addGroupModal && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
            >
              <Typography variant="body2" color="text.secondary">
                Under {addGroupModal.typeName}. Enter a name for the new group
                (e.g. Tax Assistants), or leave blank to use the type name.
              </Typography>
              <TextField
                label="Group name (optional)"
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                fullWidth
                disabled={isSaving}
                placeholder={addGroupModal.typeName}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddGroupModal(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleAddGroup}
            variant="contained"
            disabled={isSaving}
          >
            Add group
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add member modal */}
      <Dialog
        open={!!addModal}
        onClose={() => !isSaving && setAddModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add member</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setUserSearchOpen(true)}
              disabled={isSaving}
            >
              {selectedUser
                ? `${selectedUser.first_name} ${selectedUser.last_name}`
                : 'Select user'}
            </Button>
            {addModal && (
              <RoleSelect
                groupTypeId={addModal.groupTypeId}
                value={positionId}
                onChange={setPositionId}
                disabled={isSaving}
              />
            )}
            <TextField
              label="Start date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
              disabled={isSaving}
            />
            <TextField
              label="End date (optional)"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              disabled={isSaving}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddModal(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleAddMember}
            variant="contained"
            disabled={isSaving || !selectedUser || !positionId || !startDate}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit member modal */}
      <Dialog
        open={!!editModal}
        onClose={() => !isSaving && setEditModal(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit member</DialogTitle>
        <DialogContent>
          {editModal && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
            >
              <Typography variant="body1">
                {editModal.member.user_name}
              </Typography>
              <TextField
                label="Start date"
                type="date"
                value={editStartDate}
                onChange={(e) => setEditStartDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={isSaving}
              />
              <TextField
                label="End date (optional)"
                type="date"
                value={editEndDate}
                onChange={(e) => setEditEndDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                fullWidth
                disabled={isSaving}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModal(null)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateMember}
            variant="contained"
            disabled={isSaving}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <UserSearchModal
        modalOpen={userSearchOpen}
        setModalOpen={(open) => {
          if (!open) setSelectedUser(null);
          setUserSearchOpen(open);
        }}
        userObjectRet={true}
        setUser={(user) => {
          setSelectedUser({
            id: user.id,
            first_name: user.first_name,
            last_name: user.last_name,
          });
          setUserSearchOpen(false);
        }}
        activeMembers={true}
        statusFilter="active"
      />
    </Grid>
  );
};

function EOGroupSection({
  group,
  groupTypeId,
  onOpenAddMember,
  onOpenEditMember,
  onDeleteMember,
  isSaving,
}: {
  group: EOGroup;
  groupTypeId: string;
  onOpenAddMember: (groupId: string, groupTypeId: string) => void;
  onOpenEditMember: (groupId: string, member: EOGroupMember) => void;
  onDeleteMember: (groupId: string, member: EOGroupMember) => void;
  isSaving: boolean;
}) {
  return (
    <Grid container spacing={1}>
      {group.members && group.members.length > 0 ? (
        group.members.map((member) => (
          <Grid item xs={12} key={member.id}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 1,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              <Box>
                <Typography variant="body2">
                  {member.user_id ? (
                    <Link
                      href={`/user/${member.user_id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                      color="inherit"
                    >
                      {member.user_name}
                    </Link>
                  ) : (
                    member.user_name
                  )}{' '}
                  — {member.role_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {member.start_date}
                  {member.end_date ? ` – ${member.end_date}` : ''}
                </Typography>
              </Box>
              <Box>
                <IconButton
                  size="small"
                  onClick={() => onOpenEditMember(group.id, member)}
                  disabled={isSaving}
                  aria-label="Edit member"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => onDeleteMember(group.id, member)}
                  disabled={isSaving}
                  aria-label="Remove member"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </Grid>
        ))
      ) : (
        <Grid item xs={12}>
          <Typography variant="body2" color="text.secondary">
            No members yet.
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} sx={{ pt: 1 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={() => onOpenAddMember(group.id, groupTypeId)}
          disabled={isSaving}
        >
          Add member
        </Button>
      </Grid>
    </Grid>
  );
}

function RoleSelect({
  groupTypeId,
  value,
  onChange,
  disabled,
}: {
  groupTypeId: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const { data: roles = [] } = useApiGet(
    ['group-roles', groupTypeId],
    () => getGroupRolesByType(groupTypeId),
    { enabled: !!groupTypeId, retry: 1 }
  );
  return (
    <FormControl fullWidth size="small" disabled={disabled}>
      <InputLabel>Role</InputLabel>
      <Select
        value={value}
        label="Role"
        onChange={(e) => onChange(e.target.value)}
      >
        {roles.map((r: { id: string; name: string }) => (
          <MenuItem key={r.id} value={r.id}>
            {r.name}
          </MenuItem>
        ))}
        {roles.length === 0 && (
          <MenuItem value="" disabled>
            No roles defined
          </MenuItem>
        )}
      </Select>
    </FormControl>
  );
}

export default ExecutiveOffice;
